/**
 * Loads the plugin chunks an organization has enabled.
 *
 * Each org's `plugins` array (from the API) lists which feature keys are
 * enabled. This module validates those keys against `registry`, lazy-loads the
 * matching modules, and publishes the result to `orgPlugins.ts` for synchronous
 * consumers. It is the only shared module that depends on the registry, so a
 * plugin hot update stops here.
 */
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";

import {
  isChunkLoadError,
  recordChunkLoadFailure,
} from "@features/appVersion/deployVersionGuard";

import { registry } from "../../plugins/registry";
import {
  type OrgLike,
  getPluginEnablementKey,
  publishActivePlugins,
} from "./orgPlugins";
import type { BrandhubPlugin } from "./types";

const PLUGIN_KEY_CACHE = new Map<string, (keyof typeof registry)[]>();

const loadedPlugins = new Map<keyof typeof registry, BrandhubPlugin>();
const pluginLoadPromises = new Map<
  keyof typeof registry,
  Promise<BrandhubPlugin>
>();

/**
 * The latest configuration this module instance has resolved per organization,
 * so a dev hot update can resolve them again. Keyed by ID rather than by plugin
 * set, so an org whose enabled plugins change mid-session does not leave a stale
 * set behind to resolve.
 */
const loadedOrganizations = new Map<string, OrgLike>();

/**
 * Per-org generation so an overlapping load for a previous enablement cannot
 * publish after a newer load for the same organization has already started.
 */
const loadGenerationByOrg = new Map<string, number>();

function isRegisteredPluginKey(
  plugin: string
): plugin is keyof typeof registry {
  return Object.prototype.hasOwnProperty.call(registry, plugin);
}

function captureInvalidPluginKeys(organization: OrgLike) {
  const invalidKeys = organization.plugins.filter(
    (plugin) => !isRegisteredPluginKey(plugin)
  );
  if (invalidKeys.length > 0) {
    console.error("Invalid plugin keys:", invalidKeys.join(", "));
    Sentry.captureMessage(`Invalid plugin keys: ${invalidKeys.join(", ")}`, {
      level: "error",
    });
  }
}

// Cached - returns active valid plugin keys for an organization
function getActivePluginKeys(organization: OrgLike): (keyof typeof registry)[] {
  const cacheKey = getPluginEnablementKey(organization);
  const cached = PLUGIN_KEY_CACHE.get(cacheKey);
  if (cached) {
    return cached;
  }

  captureInvalidPluginKeys(organization);

  const activeKeys = organization.plugins.filter(isRegisteredPluginKey);
  PLUGIN_KEY_CACHE.set(cacheKey, activeKeys);
  return activeKeys;
}

/**
 * Loads the current org's plugin chunks before synchronous consumers read them.
 * Fails silently if the plugin fails to load.
 */
export async function loadActivePlugins(
  organization: OrgLike
): Promise<readonly BrandhubPlugin[]> {
  const generation = (loadGenerationByOrg.get(organization.id) ?? 0) + 1;
  loadGenerationByOrg.set(organization.id, generation);

  const keys = getActivePluginKeys(organization);
  const results = await Promise.all(
    keys.map(async (key) => {
      try {
        return {
          status: "loaded",
          plugin: await loadPlugin(key),
        } as const;
      } catch (error: unknown) {
        return { status: "failed", key, error } as const;
      }
    })
  );
  const plugins: BrandhubPlugin[] = [];

  for (const result of results) {
    switch (result.status) {
      case "loaded":
        plugins.push(result.plugin);
        break;
      case "failed": {
        if (isChunkLoadError(result.error)) {
          recordChunkLoadFailure("plugin-load", result.error);
          break;
        }

        // Plugin failed to load for other reason, report it silently to Sentry
        const error =
          result.error instanceof Error
            ? result.error
            : new Error(`Unable to load plugin "${result.key}".`);
        console.error("Unable to load plugin", result.key, error);
        Sentry.captureException(error, {
          tags: {
            organizationId: organization.id,
            plugin: result.key,
          },
        });
        break;
      }
      default: {
        const unreachable: never = result;
        throw new Error(`Unhandled plugin load result: ${unreachable}`);
      }
    }
  }

  if (loadGenerationByOrg.get(organization.id) !== generation) {
    return plugins;
  }

  loadedOrganizations.set(organization.id, organization);
  publishActivePlugins(organization, plugins);
  return plugins;
}

export function useLoadActivePluginsQuery(organization?: OrgLike) {
  return useQuery({
    queryKey: [
      "organization-plugins",
      organization ? getPluginEnablementKey(organization) : null,
    ],
    queryFn: () => {
      if (!organization) {
        throw new Error("Cannot load plugins without an organization.");
      }

      return loadActivePlugins(organization);
    },
    staleTime: Infinity,
    enabled: organization !== undefined,
  });
}

function loadPlugin(key: keyof typeof registry): Promise<BrandhubPlugin> {
  const existingPlugin = loadedPlugins.get(key);
  if (existingPlugin) {
    return Promise.resolve(existingPlugin);
  }

  const existingLoad = pluginLoadPromises.get(key);
  if (existingLoad) {
    return existingLoad;
  }

  const load = registry[key]()
    .then(({ default: plugin }) => {
      loadedPlugins.set(key, plugin);
      return plugin;
    })
    .finally(() => {
      pluginLoadPromises.delete(key);
    });

  pluginLoadPromises.set(key, load);
  return load;
}

/**
 * Dev only. Resolves the given organizations again through this module's
 * registry import. The previous module instance passes its organizations to the
 * updated one on a hot update, so the updated import graph — which includes the
 * edited plugin chunk — is what gets published.
 */
export async function reloadPluginsAfterHotUpdate(
  organizations: readonly OrgLike[]
): Promise<void> {
  for (const organization of organizations) {
    await loadActivePlugins(organization);
  }
}

// Dev only. A plugin edit invalidates the plugin chunk, the registry, and this
// module, but not the store the resolved plugins live in. Re-resolving through
// the updated instance republishes them, so slots render the edited plugin
// while the rest of the app keeps its state. Only plugins whose module actually
// changed get a new identity, so unchanged plugins do not remount.
if (import.meta.hot) {
  import.meta.hot.accept((updated) => {
    if (!updated) {
      window.location.reload();
      return;
    }

    void updated.reloadPluginsAfterHotUpdate([...loadedOrganizations.values()]);
  });
}
